/** Fetch wrapper with an in-memory ETag cache and typed errors. Same-origin API under /api. */

const cache = new Map(); // url -> { etag, body }

export class ApiError extends Error {
  constructor(status, message, url) {
    super(message);
    this.status = status;
    this.url = url;
  }
}

export function buildUrl(path, params) {
  const url = new URL(path, window.location.origin);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === "" || v === false) continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.pathname + url.search;
}

export async function apiGet(path, params) {
  const url = buildUrl(path, params);
  const cached = cache.get(url);
  const headers = { Accept: "application/json" };
  if (cached?.etag) headers["If-None-Match"] = cached.etag;
  let resp;
  try {
    resp = await fetch(url, { headers });
  } catch (err) {
    throw new ApiError(0, "Não foi possível conectar ao servidor.", url);
  }
  if (resp.status === 304 && cached) return cached.body;
  if (!resp.ok) {
    let detail = `Erro ${resp.status}`;
    try {
      const j = await resp.json();
      if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
    } catch {
      /* ignore */
    }
    if (resp.status === 404) detail = "Não encontrado.";
    throw new ApiError(resp.status, detail, url);
  }
  const body = await resp.json();
  const etag = resp.headers.get("etag");
  if (etag) cache.set(url, { etag, body });
  return body;
}

export async function apiPost(path, body) {
  const url = buildUrl(path);
  let resp;
  try {
    resp = await fetch(url, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (err) {
    throw new ApiError(0, "Não foi possível conectar ao servidor.", url);
  }
  if (!resp.ok) {
    let detail = `Erro ${resp.status}`;
    try {
      const j = await resp.json();
      if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
    } catch {
      /* ignore */
    }
    if (resp.status === 404) detail = "Não encontrado.";
    throw new ApiError(resp.status, detail, url);
  }
  return resp.json();
}

export function errorBanner(err) {
  const msg = err?.message || String(err);
  return `<div class="banner banner--error" role="alert">${escape(msg)}</div>`;
}

export function loadingBanner(text = "Carregando…") {
  return `<div class="banner banner--loading" aria-busy="true">${escape(text)}</div>`;
}

function escape(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

// --- typed helpers ---------------------------------------------------------------

export const api = {
  health: () => apiGet("/api/health"),
  methodology: () => apiGet("/api/meta/methodology"),
  subjects: () => apiGet("/api/subjects"),
  search: (params) => apiGet("/api/search", params),
  propositions: (params) => apiGet("/api/propositions", params),
  proposition: (id) => apiGet(`/api/propositions/${id}`),
  tramitacoes: (id, params) => apiGet(`/api/propositions/${id}/tramitacoes`, params),
  propositionRollCalls: (id) => apiGet(`/api/propositions/${id}/roll-calls`),
  members: (params) => apiGet("/api/members", params),
  member: (id, params) => apiGet(`/api/members/${id}`, params),
  memberVotes: (id, params) => apiGet(`/api/members/${id}/votes`, params),
  memberPropositions: (id, params) => apiGet(`/api/members/${id}/propositions`, params),
  rollCalls: (params) => apiGet("/api/roll-calls", params),
  rollCall: (id) => apiGet(`/api/roll-calls/${id}`),
  parties: (params) => apiGet("/api/parties", params),
  summarize: (id) => apiPost(`/api/propositions/${id}/summary`),
  /** Every tramitação of a proposition, oldest first (pages of 200; a few PECs have 1000+ entries). */
  tramitacoesAll: async (id, { onProgress, pageSize = 200, maxPages = 25 } = {}) => {
    const items = [];
    for (let page = 1; page <= maxPages; page++) {
      const data = await apiGet(`/api/propositions/${id}/tramitacoes`, { page, page_size: pageSize, order: "asc" });
      items.push(...(data.items || []));
      onProgress?.(items.length, data.total ?? items.length);
      if (!data.has_next) break;
    }
    return items;
  },
};
